import * as React from "react";
import { inject, observer } from "mobx-react";
import { AdvertisingStore } from "src/stores/AdvertisingStore";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { IntentMedia } from "src/components/utility/Ads/IntentMedia";
import { RouteStore, ExperimentStore } from "bernie-core";
import { AdsTarget } from "components/flexComponents/Ad/typings";

interface AdScriptsProps {
  advertising?: AdvertisingStore;
  routeStore?: RouteStore;
  experiment?: ExperimentStore;
  context: ExtendedContextStore;
  pageName: string;
}

@inject("advertising", "routeStore", "experiment")
@observer
export class AdScripts extends React.Component<AdScriptsProps> {
  private window: any;

  constructor(props: AdScriptsProps) {
    super(props);
    this.handleAdBlockDetect = this.handleAdBlockDetect.bind(this);
  }

  public componentDidMount() {
    this.window = window;
    this.loadAdScripts();
    this.activateBreakpointsFramework();
    this.updateExperimentsFramework();
    this.updateChanges();
  }

  public componentWillUnmount() {
    // Clear the ADS cache.
    this.window.meso = this.window.meso || {};
    this.window.meso.initialize = this.window.meso.initialize || [];
    this.window.meso.initialize.push({ type: "clear" });
  }

  /**
   * Load ads related scripts onto the page.
   */
  private loadAdScripts() {
    this.loadAdBlockDetectScript();
    this.loadAdsLoaderScript();
    this.loadAdsLoaderFallbackScript();
  }

  /**
   * Loads the script to detect whether AdBlock is enabled on the page.
   */
  private loadAdBlockDetectScript() {
    const { advertising } = this.props;
    if (!advertising) {
      return;
    }

    const adBlockDetectScript = document.createElement("script");
    adBlockDetectScript.src = advertising.adBlockDetect;
    adBlockDetectScript.onerror = adBlockDetectScript.onload = this.handleAdBlockDetect;
    document.body.appendChild(adBlockDetectScript);
  }

  /**
   * Loads ads loader script on the page.
   */
  private loadAdsLoaderScript() {
    const { advertising } = this.props;
    if (!advertising) {
      return;
    }

    const script = document.createElement("script");
    script.src = advertising.adsloader;
    script.type = "module";
    document.body.appendChild(script);
  }

  /**
   * Loads the fallback script for ads loader. This is for older es5 browsers that don't support module type.
   */
  private loadAdsLoaderFallbackScript() {
    const { advertising } = this.props;
    if (!advertising) {
      return;
    }

    const fallbackScript = document.createElement("script");
    fallbackScript.src = advertising.adsloaderFallback;
    fallbackScript.defer = true;
    fallbackScript.noModule = true;
    document.body.appendChild(fallbackScript);
  }

  /**
   * Returns the target comprising of pageId, siteId, locale, variant, brand, canTrack and canSell.
   */
  private getTarget(): AdsTarget {
    const { context, pageName, routeStore, advertising } = this.props;
    const { canTrack, canSell } = this.getPrivacyConsent();

    return {
      pageId: advertising?.adPageIdOverride || pageName,
      siteId: context?.site?.id.toString(),
      locale: context?.locale,
      variant: routeStore?.queryParams?.overrideAdsPageVariant || "pwa",
      brand: context?.site?.brand,
      canTrack,
      canSell,
    };
  }

  /**
   * Checks the passed target against the target object set in AdvertisingStore. If they
   * differ, the new target is set to the AdvertisingStore, and the page is also initialized
   * with new ads target information (such as - an updated variant, page name, locale etc).
   */
  private updateTargetIfChanged(): void {
    const { advertising } = this.props;
    const target = this.getTarget();

    if (advertising?.hasTargetChanged(target)) {
      this.window.meso = this.window.meso || {};
      this.window.meso.initialize = this.window.meso.initialize || [];
      this.window.meso.initialize.push(target);

      advertising.updateTarget(target);
    }
  }

  /**
   * Checks the context to see if the user enables tracking for ads
   * Alternatively, checks OIP cookie which is set from the bernie banner
   */
  private getPrivacyConsent() {
    const { context } = this.props;
    const optInPrivacy = context?.site?.optInPrivacyInfo;
    const canTrack = optInPrivacy?.canTrack || false;
    // canSell defaults to true unless explicitly false
    const canSell = !(optInPrivacy?.canSell === false);

    return {
      canTrack,
      canSell,
    };
  }

  /**
   * Push the application to the breakpoints framework.
   */
  private activateBreakpointsFramework() {
    if (this.window) {
      this.window.meso = this.window.meso || {};
      this.window.meso.breakpoints = this.window.meso.breakpoints || [];
      this.window.meso.breakpoints.push({ type: "platform", platform: "bernie" });
    }
  }

  /**
   * Update the window.meso.targeting object
   */
  private updateTargeting() {
    if (this.window) {
      this.window.meso = this.window.meso || {};
      this.window.meso.targeting = this.window.meso.targeting || [];
      this.window.meso.targeting.push({
        type: "update",
        targeting: {},
      });

      const { advertising } = this.props;
      if (advertising?.CMPTST) {
        this.window.meso.targeting.push({
          type: "update",
          targeting: { CMPTST: advertising.CMPTST },
        });
      }
    }
  }

  /**
   * Push experiments to window.meso.experiments
   */
  private updateExperimentsFramework() {
    if (this.window) {
      this.window.meso = this.window.meso || {};
      this.window.meso.experiments = this.window.meso.experiments || [];
      this.window.meso.experiments.push({
        type: "update",
        experiments: this.parseExperiments(),
      });
    }
  }

  private parseExperiments() {
    const result = {};
    const exposures = this.props.experiment?.exposures || {};
    // Map experiment name to bucket id in result.
    Object.keys(exposures).forEach((key) => {
      if (exposures[key].hasOwnProperty("bucket")) {
        result[key] = exposures[key].bucket;
      }
    });

    return result;
  }

  private handleAdBlockDetect() {
    const { advertising } = this.props;
    if (advertising) {
      advertising.adBlockerEnabled = typeof this.window.adblock === "undefined" ? true : Boolean(this.window.adblock);
    }
  }

  /**
   * Updates the changes in target, targeting and Cmptst as applicable.
   */
  private updateChanges() {
    const { advertising } = this.props;
    const mesoInitialized = this.window?.meso?.initialize?.isEventQueue;

    if (this.window && advertising?.includeAdLoader && !mesoInitialized) {
      this.updateTargetIfChanged();
      this.updateTargeting();
    }
  }

  public render() {
    const { context } = this.props;
    this.updateChanges();

    return <IntentMedia context={context} />;
  }
}
